import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dismissEnrollmentNotice,
  listOpenEnrollmentNotices,
  type EnrollmentNotice,
} from 'shared/data/repositories/enrollmentNoticesRepository';
import { useAppSelector } from 'hooks/useAppStore';

export const enrollmentNoticeKeys = {
  open: ['enrollment-notices', 'open'] as const,
};

export function useOpenEnrollmentNotices() {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  return useQuery<EnrollmentNotice[]>({
    queryKey: enrollmentNoticeKeys.open,
    queryFn: () => listOpenEnrollmentNotices(3),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
}

export function useDismissEnrollmentNotice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dismissEnrollmentNotice(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: enrollmentNoticeKeys.open });
      const previous = queryClient.getQueryData<EnrollmentNotice[]>(enrollmentNoticeKeys.open);
      queryClient.setQueryData<EnrollmentNotice[]>(
        enrollmentNoticeKeys.open,
        (curr) => (curr ?? []).filter((item) => item.id !== id),
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(enrollmentNoticeKeys.open, ctx.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: enrollmentNoticeKeys.open });
    },
  });
}
